import { ref, get } from 'firebase/database';
import { db } from '../../config/firebase';

import {
  setStdFilesData,
  setQcFilesData,
  setUnkFilesData,
  saveStdPriority,
  saveReportingPeaks,
  saveAllReportingPeaks,
  saveSigma,
  saveGamma,
  saveBuggingPeaks,
  saveAreaUnderPeaks,
  savePeaksObject,
  saveAreaUnderPeaksQc,
  saveAreaUnderPeaksUnk,
  saveStdXPeaks,
  saveQcXPeaks,
  saveUnkXPeaks,
} from '../../reduxSlices/results/resultsSlice';

// Helper to download JSON from a Storage URL
async function downloadJsonFromUrl(url, retryCount = 1) {
  try {
    const response = await fetch(url, { credentials: 'omit' });
    if (!response.ok) {
      const text = await response.text();
      if (response.status === 403 && retryCount > 0) {
        console.warn('[hydrateConfigToRedux] Access denied (403). Retrying...:', url);
        return await downloadJsonFromUrl(url, retryCount - 1); // Retry once
      }
      console.error('[hydrateConfigToRedux] Fetch failed:', url, 'Status:', response.status, 'Body:', text);
      throw new Error('Failed to fetch: ' + url + ' Status: ' + response.status + ' Body: ' + text);
    }
    const json = await response.json();
    return json;
  } catch (err) {
    console.error('[hydrateConfigToRedux] Error fetching URL:', url, err);
    throw err;
  }
}


// Hydrate a file object: replace *Url fields with downloaded arrays
async function hydrateFileObj(fileObj, onFileHydrated) {
  const hydrated = { ...fileObj };
  for (const key of Object.keys(fileObj)) {
    if (key.endsWith('Url') && typeof fileObj[key] === 'string') {
      const arrayKey = key.replace('Url', '');
      try {
        hydrated[arrayKey] = await downloadJsonFromUrl(fileObj[key]);
      } catch (err) {
        if (err.message.includes('403')) {
          console.warn(`[hydrateConfigToRedux] Skipping hydration for '${arrayKey}' due to access denial:`, fileObj[key]);
        } else {
          throw err; // Re-throw other errors
        }
      }
      delete hydrated[key];
    }
  }
  if (onFileHydrated) onFileHydrated();
  return hydrated;
}

// Hydrate all files in a fileType object, with progress callback
async function hydrateFilesObj(filesObj, onFileHydrated) {
  if (!filesObj || typeof filesObj !== 'object') return {};
  const hydrated = {};
  const entries = Object.entries(filesObj);
  for (const [fileName, fileObj] of entries) {
    hydrated[fileName] = await hydrateFileObj(fileObj, onFileHydrated);
  }
  return hydrated;
}

/**
 * Hydrate config from DB and Storage, then dispatch to Redux.
 * @param {string} configId
 * @param {string} uid

 * @param {function} dispatch
 * @param {function} [onProgress] - Optional progress callback (0-100)
 */
export async function hydrateConfigToRedux(configId, uid, dispatch, onProgress) {
  if (!configId || !uid) throw new Error('Missing configId or uid');
  // 1. Fetch config from DB
  const dbRef = ref(db, `users/${uid}/results/${configId}`);
  const snapshot = await get(dbRef);
  if (!snapshot.exists()) {
    console.error('[hydrateConfigToRedux] Config not found in DB:', configId);
    throw new Error('Config not found');
  }
  const data = snapshot.val();

  // 2. Hydrate files with progress
  const stdEntries = data.stdFiles ? Object.entries(data.stdFiles) : [];
  const qcEntries = data.qcFiles ? Object.entries(data.qcFiles) : [];
  const unkEntries = data.unkFiles ? Object.entries(data.unkFiles) : [];
  const totalFiles = stdEntries.length + qcEntries.length + unkEntries.length;
  let hydratedCount = 0;
  function updateProgress() {
    hydratedCount++;
    if (onProgress && totalFiles > 0) {
      onProgress(Math.round((hydratedCount / totalFiles) * 100));
    }
  }
  const stdFiles = await hydrateFilesObj(data.stdFiles, updateProgress);
  const qcFiles = await hydrateFilesObj(data.qcFiles, updateProgress);
  const unkFiles = await hydrateFilesObj(data.unkFiles, updateProgress);
  if (onProgress) onProgress(100);

  // 3. Dispatch to Redux
  dispatch(setStdFilesData(Object.entries(stdFiles).map(([fileName, fileData]) => ({ fileName, fileData }))));
  dispatch(setQcFilesData(Object.entries(qcFiles).map(([fileName, fileData]) => ({ fileName, fileData }))));
  dispatch(setUnkFilesData(Object.entries(unkFiles).map(([fileName, fileData]) => ({ fileName, fileData }))));
  if (data.stdFilesPriority) dispatch(saveStdPriority(data.stdFilesPriority));
  if (data.reportingPeaks) dispatch(saveReportingPeaks(data.reportingPeaks));
  if (data.allReportingPeaks) dispatch(saveAllReportingPeaks(data.allReportingPeaks));
  if (data.sigma) dispatch(saveSigma(data.sigma));
  if (data.gamma) dispatch(saveGamma(data.gamma));
  if (data.buggingPeaks) dispatch(saveBuggingPeaks(data.buggingPeaks));
  if (data.areaUnderPeaks) dispatch(saveAreaUnderPeaks(data.areaUnderPeaks));
  if (data.peaksObject) dispatch(savePeaksObject(data.peaksObject));
  if (data.areaUnderPeaksQc) dispatch(saveAreaUnderPeaksQc(data.areaUnderPeaksQc));
  if (data.areaUnderPeaksUnk) dispatch(saveAreaUnderPeaksUnk(data.areaUnderPeaksUnk));
  if (data.stdXPeaks) dispatch(saveStdXPeaks(data.stdXPeaks));
  if (data.qcXPeaks) dispatch(saveQcXPeaks(data.qcXPeaks));
  if (data.unkXPeaks) dispatch(saveUnkXPeaks(data.unkXPeaks));
}
